import React, { useEffect, useState } from "react";
import { PageHeading } from "../../components/PageHeading";
import { useTeamsStore } from "../../store/teamsStore";
import { useSeasonsStore } from "../../store/seasonsStore";
import { Busy } from "../../components/Busy";
import { classNames } from "../../utils/appUtils";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Text } from "../../components/catalyst/text";
import useDraftingStore from "../../store/draftingStore";
import { WsConnectionStatus } from "../../components/WsConnectionStatus";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAuthStore from "../../store/authStore";
import DraftingMyTeam from "../../components/drafting/DraftingMyTeam";
import { DraftSelectPlayer } from "../../components/drafting/DraftSelectPlayers";
import { DescriptionTerm } from "../../components/catalyst/description-list";
import { DraftSelectShortlisted } from "../../components/drafting/DraftSelectShortlisted";
import { useConfirmationDialog } from "../../components/ConfirmDialog";

const Draft: React.FC = () => {
  const { currentSeason } = useSeasonsStore();
  const { myTeam, isBusy, busyMessage, getTeams, draftPlayerIntoMyTeam } =
    useTeamsStore();
  const { currentUser } = useAuthStore();
  const { availablePlayers, getAvailablePlayersForDraft, currentSeasonDraft } =
    useDraftingStore();

  const [search, setSearch] = useState("");
  const [isDraftingOpen, setIsDraftingOpen] = useState(false);
  const navigate = useNavigate();
  const { openDialog } = useConfirmationDialog();

  useEffect(() => {
    if (currentSeason) {
      if (!currentSeason.seasonDraftingInProgress) {
        // If drafting is not in progress, redirect to the home page
        toast.error("Drafting is not in progress for the current season.");
        navigate("/");
      }
      getTeams(currentSeason.id);
      getAvailablePlayersForDraft(currentSeason.id);
    }
  }, [currentSeason]);

  useEffect(() => {
    if (
      currentUser &&
      currentUser.role !== "playingTeamOwner" &&
      currentUser.role !== "nonPlayingTeamOwner" &&
      currentUser.role !== "captain"
    ) {
      toast.error("You are not authorized to view this page.");
      navigate("/");
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentSeasonDraft && myTeam) {
      const closeDrafting =
        currentSeason?.teamSize &&
        myTeam?.playersColl.length === currentSeason?.teamSize;
      if (closeDrafting) {
        setIsDraftingOpen(false);
      } else {
        setIsDraftingOpen(
          currentSeasonDraft.currentDraftingTeamId === myTeam.id
        );
      }
    }
  }, [currentSeasonDraft, myTeam, availablePlayers]);

  useEffect(() => {
    // Trigger a re-render when availablePlayers changes
    setSearch(search);
  }, [availablePlayers]);

  const filteredMembers = availablePlayers
    ? availablePlayers.filter(
        (member) =>
          !member.isCoreMember &&
          !member.isCaptain &&
          !member.isPlayer &&
          !!member.playerHandicap &&
          member.fullName.toLowerCase().includes(search.toLowerCase())
      )
    : [];
  const onMembersSelected = (member: any) => {
    if (!currentSeason) return;
    openDialog({
      title: "Draft Player",
      message: `Are you sure you want to draft ${member.fullName} into your team?`,
      onConfirm: () => {
        draftPlayerIntoMyTeam(currentSeason.id, member);
      },
    });
  };

  return (
    <div className="pt-2 h-[calc(100vh-100px)] flex flex-col">
      <div className="flex-none dark:bg-slate-700 p-2 pb-0 rounded-md">
        <PageHeading
          heading="Drafting"
          subText=" - Drafting players into teams."
          rightContent={<WsConnectionStatus />}
        />
        <div className="flex my-2 justify-between">
          <Text>Select a player for your team when enabled.</Text>
          <div className="flex items-center justify-center">
            {currentSeason?.teamSize &&
              myTeam?.playersColl.length === currentSeason?.teamSize && (
                <Text className="mr-2">
                  Your team is full. Please wait for the draft to end.
                </Text>
              )}
            {/* {isDraftingOpen && (
              <DisplayTimer timeLeftInSeconds={timeLeftInSeconds} />
            )}
            {!isDraftingOpen && <DisplayTimer timeLeftInSeconds={0} />} */}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-auto min-h-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg ">
              <DraftingMyTeam myTeam={myTeam} currentUser={currentUser} />
            </div>
          </div>
          <div className="col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg ">
              <div>
                <DescriptionTerm>
                  Shortlisted Players: {myTeam?.shortlistedPlayersColl.length}
                </DescriptionTerm>

                {myTeam &&
                  myTeam.shortlistedPlayersColl &&
                  myTeam.shortlistedPlayersColl.map((player, index) => (
                    <div
                      className={classNames(
                        "rounded-lg flex items-center",
                        index % 2 === 0
                          ? "bg-gray-100 dark:bg-gray-700"
                          : "bg-white dark:bg-gray-800"
                      )}
                      key={player.playerId}
                    >
                      <Text className="p-2 text-sm font-medium">
                        {index + 1}.
                      </Text>
                      <div className="flex-1">
                        <DraftSelectShortlisted
                          key={`shortlisted-${player.playerId}`}
                          player={player}
                          index={index}
                          onMembersSelected={onMembersSelected}
                          disabled={!isDraftingOpen}
                          availablePlayers={availablePlayers}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg ">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 p-2">
                Available Players
              </h2>
              <div className="relative flex flex-1 h-8 mt-3 mb-2">
                <label htmlFor="search-field" className="sr-only">
                  Search
                </label>
                <MagnifyingGlassIcon
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400 dark:text-gray-200"
                />
                <input
                  id="search-field"
                  name="search"
                  type="search"
                  placeholder="Search players..."
                  onChange={(e) => setSearch(e.target.value)}
                  className="block h-full w-full border-0 py-0 pl-8 pr-0 text-gray-900 dark:text-gray-200 dark:placeholder:text-gray-200 bg-white dark:bg-gray-600 placeholder:text-gray-400 focus:ring-0 sm:text-sm rounded-md"
                />
              </div>
              {filteredMembers &&
                filteredMembers.map((player: any, index: number) => (
                  <div
                    className={classNames(
                      "rounded-lg flex items-center",
                      index % 2 === 0
                        ? "bg-gray-100 dark:bg-gray-700"
                        : "bg-white dark:bg-gray-800"
                    )}
                    key={player.id}
                  >
                    <Text className="p-2 text-sm font-medium">
                      {index + 1}.
                    </Text>
                    <div className="flex-1">
                      <DraftSelectPlayer
                        key={`available-${player.id}`}
                        player={player}
                        index={index}
                        onMembersSelected={onMembersSelected}
                        disabled={!isDraftingOpen}
                      />
                    </div>
                  </div>
                ))}
              {filteredMembers.length === 0 &&
                availablePlayers &&
                availablePlayers.length > 0 && (
                  <Text className="text-center">
                    No players found{" "}
                    {search && <>for search term: "{search}"</>}
                  </Text>
                )}
            </div>
          </div>
        </div>
      </div>

      {isBusy && (
        <Busy busyMessage={busyMessage ? busyMessage : "Please wait..."} />
      )}
    </div>
  );
};

export default Draft;
